import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import DataTable, { ColumnDef } from '../components/table/DataTable';

type FicheSejour = {
  typefiche: string;
  nomproprio: string;
  prenproprio: string;
  codpost: string;
  libpays: string;
  email: string;
  nombat: string;
  tiersid: number;
  datdeb: string | null;
  datfin: string | null;
};

// Libellés lisibles
const typeLabels: Record<string, string> = {
  ZONETECH: "Zone technique",
  LISTATT: "Liste d'attente",
  CONTRAT: "Contrat",
  PASSAGE: "Passage",
  CLIENTDIV: "Client divers"
};




export default function FicheSejourPage() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [rows, setRows] = useState<FicheSejour[]>([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');

  // Colonnes du tableau
  const cols: ColumnDef<FicheSejour>[] = [
    { key: 'typefiche', header: 'Type', sortable: true, render: v => getLabel(v) },
    { key: 'nomproprio', header: 'Nom', sortable: true },
    { key: 'prenproprio', header: 'Prénom', sortable: true },
    { key: 'codpost', header: 'Code Postal', sortable: true },
    { key: 'libpays', header: 'Pays', sortable: true },
    { key: 'email', header: 'Email', sortable: true },
    { key: 'nombat', header: 'Bateau', sortable: true },
    { key: 'datdeb', header: 'Début', sortable: true },
    { key: 'datfin', header: 'Fin', sortable: true },
    ];


  // Chargement des données
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setErr('');
        const res = await api.get('/fiche-sejour', {
          params: { clientCode: localStorage.getItem('clientCode') }
        });
        setRows(res.data.items || []);
      } catch (e: any) {
        setErr(e?.response?.data?.error || 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

    // Liste dynamique des types avec renommage
 const types = useMemo(() => {
  const set = new Set(rows.map(r => (r.typefiche ?? '').trim()).filter(Boolean));
  return ['ALL', ...Array.from(set)];
}, [rows]);



  // Filtrage côté client
  const filteredRows = useMemo(() => {
  return filter === 'ALL'
    ? rows
    : rows.filter(r => (r.typefiche ?? '').trim() === filter);
}, [rows, filter]);

const getLabel = (type: string) => typeLabels[type.trim()] || type.trim();

  return (
    <div className="min-h-screen flex bg-[#f9fafb] dark:bg-darkMode">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <div className="flex flex-col flex-1 lg:ml-64">
        <Header onOpenSidebar={() => setIsSidebarOpen(true)} />

        <main className="p-6 dark:bg-darkMode pt-16">
          <div className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Fiches Séjour</h1>
              <div className="flex items-center gap-2">
                {types.map((t) => (
                    <Chip key={t} active={filter === t} onClick={() => setFilter(t)} >
                        {t === 'ALL' ? 'Tous' : getLabel(t)}
                    </Chip>
                    ))}
                </div>
            </div>

            {err && <div className="text-red-500">{err}</div>}
            {loading ? (
              <div className="rounded-xl border border-gray-200 p-6 text-gray-500">Chargement…</div>
            ) : (
              <DataTable
                data={filteredRows}
                columns={cols}
                pageSize={20}
                initialSort={{ key: 'datfin', dir: 'desc' }}
                onRowClick={row => console.log('Open fiche', row.tiersid)}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

// Composant Chip
function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-sm border transition
      ${active ? 'bg-indigo-600 text-white border-indigo-600'
               : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:border-white/20  dark:bg-gray-800  dark:text-gray-100'}`}
    >
      {children}
    </button>
  );
}
